import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Link } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { UseModalResult } from '@shapeable/ui';
import { DOING_CHALK } from '../../theme';
import { MailchimpEmbed } from './mailchimp-embed';

export const MAILCHIMP_MODAL_LINK_SLUG = 'mailchimp-modal-button';
export const MAILCHIMP_MODAL_LINK_SLUG_ALT = 'mailchimp-modal-button-1';

export const isMailchimpModalSlug = (slug?: string) => (
  slug === MAILCHIMP_MODAL_LINK_SLUG || slug === MAILCHIMP_MODAL_LINK_SLUG_ALT
);

const SIDEBAR_WIDTH = 60;

export const mailchimpModalOptions = {
  color: '#000000',
  title: 'Sign Up',
  opacity: 0.5,
};

type MailchimpModalLinkContext = {
  pagePath?: string | false;
  fileUrl?: string;
  videoUrl?: string;
};

export const isMailchimpModalLink = (
  entity: Pick<Link, 'slug'>,
  { pagePath, fileUrl, videoUrl }: MailchimpModalLinkContext,
) => (
  !pagePath && !fileUrl && !videoUrl && isMailchimpModalSlug(entity.slug)
);

const modalPanelIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px) scale(0.985);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const modalPanelScrollIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ModalPanelStyles = breakpoints({
  base: css`
    animation: ${modalPanelIn} 1s cubic-bezier(0.2, 0.8, 0.2, 1);
    background: ${DOING_CHALK};
    border-radius: ${theme.UNIT(6)};
    box-sizing: border-box;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.16);
    margin: calc(64px + ${theme.UNIT(4)}) ${theme.UNIT(5)} ${theme.UNIT(5)};
    max-height: calc(100vh - 64px - ${theme.UNIT(9)});
    overflow: hidden;
    transform-origin: top center;
    width: min(100% - ${theme.UNIT(10)}, 56rem);
    will-change: transform, opacity;
  `,
  tablet: css`
    margin-top: calc(78px + ${theme.UNIT(4)});
    margin-right: calc(${theme.UNIT(5)} + ${SIDEBAR_WIDTH}px);
    max-height: calc(100vh - 78px - ${theme.UNIT(9)});
    width: min(calc(100% - ${theme.UNIT(10)} - ${SIDEBAR_WIDTH}px), 56rem);
  `,
  desktop: css`
    margin-top: 92px;
    margin-right: calc(${theme.UNIT(5)} + ${SIDEBAR_WIDTH}px);
    max-height: calc(100vh - 92px - ${theme.UNIT(5)});
    width: min(calc(100% - ${theme.UNIT(10)} - ${SIDEBAR_WIDTH}px), 56rem);
  `,
});

const ModalPanelScrollStyles = breakpoints({
  base: css`
    animation: ${modalPanelScrollIn} 1s cubic-bezier(0.16, 1, 0.3, 1) 20ms both;
    max-height: calc(100vh - 64px - ${theme.UNIT(9)});
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    -webkit-overflow-scrolling: touch;
  `,
  tablet: css`
    max-height: calc(100vh - 78px - ${theme.UNIT(9)});
  `,
  desktop: css`
    max-height: calc(100vh - 92px - ${theme.UNIT(5)});
  `,
});

const My = {
  ModalPanel: styled.div`${ModalPanelStyles}`,
  ModalPanelScroll: styled.div`${ModalPanelScrollStyles}`,
};

export const MailchimpModalContent = () => (
  <My.ModalPanel onClick={(event) => event.stopPropagation()}>
    <My.ModalPanelScroll>
      <MailchimpEmbed />
    </My.ModalPanelScroll>
  </My.ModalPanel>
);

export const showMailchimpModal = (modal: Pick<UseModalResult, 'show'>) => {
  modal.show(<MailchimpModalContent />, mailchimpModalOptions);
};
